import type { ScheduledDetailedCopy } from './scheduled-page-copy-translations';

export const scheduledDetailedCopyGermanSpanish = {
  de: {
    status: {
      upcoming: 'Bevorstehend',
      running: 'Wird ausgeführt',
      waitingBackground: 'Warten auf Hintergrundarbeit',
      succeeded: 'Erfolgreich',
      failed: 'Fehlgeschlagen',
      skipped: 'Übersprungen',
      needsAttention: 'Überprüfung nötig',
      unknownOutcome: 'Ergebnis unbekannt',
      canceled: 'Abgebrochen',
      malformed: 'Nicht verfügbar',
      paused: 'Pausiert',
    },
    editor: {
      createTitle: 'Neuer Zeitplan',
      editTitle: 'Zeitplan bearbeiten',
      description:
        'Lege Aufgabe, Zeitpunkt, Kontext und unbeaufsichtigte Berechtigung fest.',
      stepOf: 'Schritt {step} von 4',
      task: 'Aufgabe',
      timing: 'Zeitplanung',
      execution: 'Ausführung',
      review: 'Prüfung',
      name: 'Name des Zeitplans',
      namePlaceholder: 'z. B. Leistungsübersicht am Montag',
      instructions: 'Anweisungen für den Agenten',
      instructionsPlaceholder:
        'Beschreibe das gewünschte Ergebnis und wo der Agent handeln darf.',
      sourceChat: 'Ausgangschat',
      scheduleThisChat: 'Diesen Chat planen',
      sourceSnapshot:
        'Beim Speichern wird eine private Momentaufnahme erstellt. Spätere Chatänderungen verändern diesen Zeitplan nicht.',
      sourceUnavailable:
        'Der Ausgangschat ist nicht verfügbar oder du hast keinen Zugriff mehr.',
      sourceChatLockedNote:
        'Zeitpläne, die einen Ausgangschat wiederverwenden, müssen den Kontext beibehalten und privat bleiben.',
      recurrence: 'Wiederholung',
      once: 'Einmalig',
      hourly: 'Stündlich',
      daily: 'Täglich',
      weekdays: 'Werktags',
      selectedWeekdays: 'Ausgewählte Wochentage',
      weekly: 'Wöchentlich',
      monthly: 'Monatlich',
      date: 'Datum',
      time: 'Ortszeit',
      dayOfMonth: 'Tag des Monats',
      whenPlaceholder: 'Morgen',
      whenInvalid: 'Gib ein Datum ein, z. B. „morgen“.',
      end: 'Ende',
      never: 'Nie',
      onDate: 'An einem Datum',
      afterOccurrences: 'Nach einer Anzahl von Läufen',
      occurrences: 'Läufe',
      nextRuns: 'Nächste Läufe',
      schedulePreview: 'Zeitplanvorschau',
      noEndDate: 'Kein Enddatum.',
      runsUntil: 'Läuft bis {date}.',
      totalRuns: 'Insgesamt {count} Läufe.',
      resolvingTime: 'Diese Ortszeit wird vom Server geprüft…',
      serverTimeRequired:
        'Der Server muss diese Ortszeit vor dem Speichern bestätigen.',
      contextMode: 'Chatkontext',
      persistent: 'Dauerhafter Kontext',
      persistentDescription:
        'Einen Zeitplan-eigenen Chat für alle Läufe wiederverwenden.',
      isolated: 'Neuer Chat pro Lauf',
      isolatedDescription: 'Jeden Lauf mit getrenntem Kontext beginnen.',
      visibility: 'Sichtbarkeit',
      private: 'Privat',
      privateDescription:
        'Nur du kannst die vollständige Anfrage, den Verlauf, die Werkzeugaktivitäten und das Ergebnis sehen.',
      workspace: 'Arbeitsbereich',
      workspaceWarning:
        'Beim Teilen im Arbeitsbereich werden die vollständige Anfrage, der Verlauf, Werkzeugaktivitäten und das Ergebnis offengelegt.',
      creditPolicy: 'Guthabenrichtlinie des Arbeitsbereichs',
      creditsAvailable:
        'Läufe verwenden das vorhandene Guthaben und die Grenzen des Arbeitsbereichs.',
      creditsUnavailable:
        'Dieser Arbeitsbereich hat derzeit kein verfügbares Guthaben. Der Zeitplan kann zur Laufzeit pausiert werden.',
      fullAuthorityTitle: 'Agentenaktionen',
      fullAuthorityDescription:
        'Bei jedem Lauf prüft der Agent deine aktuellen Rechte erneut und kann destruktive Aktionen ohne Rückfrage ausführen.',
      authorityAcknowledgment: 'Jede Aktion ohne vorherige Nachfrage erlauben.',
      authorityRequired:
        'Erlaube Agentenaktionen, um diesen Zeitplan zu erstellen. Die Ausführungen laufen unbeaufsichtigt, sodass sie später niemand genehmigen kann.',
      recurrenceUnresolved:
        'Diese Ausführungszeit konnte nicht bestätigt werden. Wähle Datum und Uhrzeit erneut aus.',
      notifications: 'Benachrichtigungen in der App',
      notificationsDescription:
        'Du wirst einmal über endgültige Ergebnisse benachrichtigt, die deine Aufmerksamkeit erfordern.',
      reviewSummary:
        'Prüfe die genaue Aufgabe und Häufigkeit, bevor du den Zeitplan erstellst.',
      required: 'Dieses Feld ist erforderlich.',
      invalid: 'Gib einen gültigen Wert ein.',
      past: 'Wähle einen Zeitpunkt in der Zukunft.',
      endBeforeStart: 'Beende den Zeitplan nach seinem ersten Lauf.',
      draftRestored:
        'Dein nicht gespeicherter Entwurf wurde wiederhergestellt.',
      tooLong: 'Dieser Wert ist zu lang.',
      createDisabled:
        'Du darfst in diesem Arbeitsbereich keine Zeitpläne erstellen.',
      saved: 'Zeitplan gespeichert',
      unsavedTitle: 'Nicht gespeicherte Änderungen verwerfen?',
      unsavedDescription:
        'Deine Änderungen an diesem Zeitplan gehen beim Schließen verloren.',
      discardChanges: 'Änderungen verwerfen',
    },
    detail: {
      title: 'Details zum Zeitplan',
      cadence: 'Häufigkeit',
      owner: 'Eigentümer',
      timezone: 'Zeitzone',
      nextRun: 'Nächster Lauf',
      lastRun: 'Letzter Lauf',
      prompt: 'Vollständige Anfrage',
      sourceContext: 'Gespeicherter Ausgangskontext',
      executionMode: 'Ausführungsmodus',
      recentUsage: 'Letzte Nutzung',
      credits: 'Guthaben',
      tools: 'Aktionen',
      history: 'Laufverlauf',
      transcript: 'Konversation',
      resultFiles: 'Ergebnisdateien',
      noResultFiles:
        'Für diesen Lauf wurden keine Ergebnisdateien aufgezeichnet.',
      noRuns: 'Noch keine Läufe.',
      noTranscript: 'Für diesen Lauf ist noch kein Verlauf verfügbar.',
      toolActivity: 'Aktivität',
      toolInput: 'Aufgabendetails',
      toolResult: 'Ergebnis',
      noToolActivity: 'Für diesen Lauf gibt es keine Aktivität.',
      loadMore: 'Mehr laden',
      preview: 'Vorschau',
      sourceScheduled: 'Zeitgesteuert',
      sourceManual: 'Manuell',
      sourceWatchdog: 'Wiederherstellung',
      roleUser: 'Benutzer',
      roleAssistant: 'Assistent',
      roleTool: 'Werkzeug',
      roleSystem: 'Systemrolle',
      runNow: 'Jetzt ausführen',
      pause: 'Pausieren',
      resume: 'Fortsetzen',
      share: 'Mit dem Arbeitsbereich teilen',
      unshare: 'Privat machen',
      cancelRun: 'Aktiven Lauf abbrechen',
      archive: 'Archivieren',
      deleteSchedule: 'Zeitplan löschen',
      openThread: 'Thread anzeigen',
      confirmArchive: 'Diesen Zeitplan archivieren?',
      confirmArchiveDescription:
        'Künftige Arbeit wird abgebrochen. Verlauf und Prüfprotokolle bleiben erhalten.',
      confirmDelete: 'Diesen Zeitplan löschen?',
      confirmDeleteDescription:
        'Der Zeitplan wird dauerhaft gelöscht. Frühere Unterhaltungen und erstellte Inhalte bleiben im Workspace.',
      confirmUnshareDescription:
        'Workspace-Mitglieder verlieren sofort den Zugriff auf Prompt, Transkript, Werkzeugaktivität und Ergebnis.',
      confirmCancel: 'Aktiven Lauf abbrechen?',
      confirmCancelDescription:
        'Der Lauf endet am nächsten sicheren Abbruchpunkt.',
      actionCompleted: 'Zeitplan aktualisiert',
      permissionDenied:
        'Du kannst diesen Zeitplan ansehen, aber nur der Eigentümer oder eine Zeitplanverwaltung kann ihn ändern.',
      needsAttention:
        'Dieser Lauf muss geprüft werden, bevor die unbeaufsichtigte Arbeit fortgesetzt wird.',
      runDetails: 'Laufdetails',
    },
    notifications: {
      completed: 'Geplanter Lauf abgeschlossen',
      failed: 'Geplanter Lauf fehlgeschlagen',
      attention: 'Geplanter Lauf muss geprüft werden',
      missed: 'Einmaliger geplanter Lauf wurde verpasst',
      viewRun: 'Lauf ansehen',
      empty: 'Noch keine Laufbenachrichtigungen.',
      unread: 'Ungelesen',
      read: 'Gelesen',
      caughtUp: 'Du hast alle Benachrichtigungen gesehen.',
    },
    errors: {
      generic: 'Etwas ist schiefgelaufen. Versuche es erneut.',
      permission: 'Du hast keine Berechtigung für diese Aktion.',
      disabled:
        'Geplante Agenten sind für diesen Arbeitsbereich noch nicht aktiviert.',
      credits: 'Der Arbeitsbereich hat nicht genügend Guthaben.',
      limit: 'Der Arbeitsbereich hat sein Limit aktiver Zeitpläne erreicht.',
      runPending:
        'Für diesen Zeitplan steht bereits ein Lauf aus oder wird ausgeführt.',
      invalidTime: 'Wähle eine gültige zukünftige Zeit.',
      notFound: 'Dieser Zeitplan oder Lauf ist nicht mehr verfügbar.',
    },
  },
  es: {
    status: {
      upcoming: 'Próximo',
      running: 'En ejecución',
      waitingBackground: 'Esperando trabajo en segundo plano',
      succeeded: 'Correcto',
      failed: 'Fallido',
      skipped: 'Omitido',
      needsAttention: 'Requiere atención',
      unknownOutcome: 'Resultado desconocido',
      canceled: 'Cancelado',
      malformed: 'No disponible',
      paused: 'En pausa',
    },
    editor: {
      createTitle: 'Nueva programación',
      editTitle: 'Editar programación',
      description:
        'Configura la tarea, el horario, el contexto y la autoridad sin supervisión.',
      stepOf: 'Paso {step} de 4',
      task: 'Tarea',
      timing: 'Horario',
      execution: 'Ejecución',
      review: 'Revisión',
      name: 'Nombre de la programación',
      namePlaceholder: 'p. ej., resumen de rendimiento del lunes',
      instructions: 'Instrucciones para el agente',
      instructionsPlaceholder:
        'Describe el resultado que debe producir el agente y dónde puede actuar.',
      sourceChat: 'Chat de origen',
      scheduleThisChat: 'Programar este chat',
      sourceSnapshot:
        'Al guardar se captura una copia privada. Los cambios posteriores del chat no modificarán esta programación.',
      sourceUnavailable:
        'El chat de origen no está disponible o ya no tienes acceso.',
      sourceChatLockedNote:
        'Los horarios que reutilizan un chat de origen deben mantener el contexto persistente y la visibilidad privada.',
      recurrence: 'Repetición',
      once: 'Una vez',
      hourly: 'Cada hora',
      daily: 'Diariamente',
      weekdays: 'Días laborables',
      selectedWeekdays: 'Días de la semana seleccionados',
      weekly: 'Semanalmente',
      monthly: 'Mensualmente',
      date: 'Fecha',
      time: 'Hora local',
      dayOfMonth: 'Día del mes',
      whenPlaceholder: 'Mañana',
      whenInvalid: 'Introduce una fecha, por ejemplo, «mañana».',
      end: 'Finalización',
      never: 'Nunca',
      onDate: 'En una fecha',
      afterOccurrences: 'Tras un número de ejecuciones',
      occurrences: 'Ejecuciones',
      nextRuns: 'Próximas ejecuciones',
      schedulePreview: 'Vista previa de la programación',
      noEndDate: 'Sin fecha de finalización.',
      runsUntil: 'Se ejecuta hasta el {date}.',
      totalRuns: '{count} ejecuciones en total.',
      resolvingTime: 'El servidor está comprobando esta hora local…',
      serverTimeRequired:
        'El servidor debe confirmar esta hora local antes de guardar.',
      contextMode: 'Contexto del chat',
      persistent: 'Contexto persistente',
      persistentDescription:
        'Reutiliza un chat propio de la programación en todas las ejecuciones.',
      isolated: 'Chat nuevo en cada ejecución',
      isolatedDescription: 'Inicia cada ejecución con un contexto aislado.',
      visibility: 'Visibilidad',
      private: 'Privado',
      privateDescription:
        'Solo tú puedes ver la solicitud completa, la conversación, la actividad de herramientas y el resultado.',
      workspace: 'Espacio de trabajo',
      workspaceWarning:
        'Compartir con el espacio de trabajo expone la solicitud completa, la conversación, la actividad de herramientas y el resultado.',
      creditPolicy: 'Política de créditos del espacio de trabajo',
      creditsAvailable:
        'Las ejecuciones usan el saldo y los límites actuales del espacio de trabajo.',
      creditsUnavailable:
        'Este espacio de trabajo no tiene créditos disponibles. La programación podría pausarse al ejecutarse.',
      fullAuthorityTitle: 'Acciones del agente',
      fullAuthorityDescription:
        'En cada ejecución, el agente vuelve a comprobar tu acceso actual y puede realizar acciones destructivas sin preguntar.',
      authorityAcknowledgment:
        'Permitir cualquier acción sin preguntar primero.',
      authorityRequired:
        'Permite las acciones del agente para crear esta programación. Las ejecuciones son desatendidas, así que después nadie podrá aprobarlas.',
      recurrenceUnresolved:
        'No se pudo confirmar esta hora de ejecución. Vuelve a elegir la fecha y la hora.',
      notifications: 'Notificaciones en la aplicación',
      notificationsDescription:
        'Recibirás una sola notificación por los resultados finales que requieran tu atención.',
      reviewSummary:
        'Revisa la tarea y la frecuencia exactas antes de crear la programación.',
      required: 'Este campo es obligatorio.',
      invalid: 'Introduce un valor válido.',
      past: 'Elige una hora futura.',
      endBeforeStart:
        'Finaliza la programación después de su primera ejecución.',
      draftRestored: 'Se restauró tu borrador sin guardar.',
      tooLong: 'Este valor es demasiado largo.',
      createDisabled:
        'No tienes permiso para crear programaciones en este espacio de trabajo.',
      saved: 'Programación guardada',
      unsavedTitle: '¿Descartar los cambios sin guardar?',
      unsavedDescription:
        'Los cambios de esta programación se perderán si cierras ahora.',
      discardChanges: 'Descartar cambios',
    },
    detail: {
      title: 'Detalles de la programación',
      cadence: 'Frecuencia',
      owner: 'Propietario',
      timezone: 'Zona horaria',
      nextRun: 'Próxima ejecución',
      lastRun: 'Última ejecución',
      prompt: 'Solicitud completa',
      sourceContext: 'Contexto de origen guardado',
      executionMode: 'Modo de ejecución',
      recentUsage: 'Uso reciente',
      credits: 'créditos',
      tools: 'acciones',
      history: 'Historial de ejecuciones',
      transcript: 'Conversación',
      resultFiles: 'Archivos de resultados',
      noResultFiles:
        'No se registraron archivos de resultados para esta ejecución.',
      noRuns: 'Todavía no hay ejecuciones.',
      noTranscript: 'Aún no hay conversación disponible para esta ejecución.',
      toolActivity: 'Actividad',
      toolInput: 'Detalles de la tarea',
      toolResult: 'Resultado',
      noToolActivity: 'No hay actividad disponible para esta ejecución.',
      loadMore: 'Cargar más',
      preview: 'Vista previa',
      sourceScheduled: 'Programada',
      sourceManual: 'Iniciada por el usuario',
      sourceWatchdog: 'Recuperación',
      roleUser: 'Usuario',
      roleAssistant: 'Asistente',
      roleTool: 'Herramienta',
      roleSystem: 'Sistema',
      runNow: 'Ejecutar ahora',
      pause: 'Pausar',
      resume: 'Reanudar',
      share: 'Compartir con el espacio de trabajo',
      unshare: 'Hacer privado',
      cancelRun: 'Cancelar ejecución activa',
      archive: 'Archivar',
      deleteSchedule: 'Eliminar programación',
      openThread: 'Ver hilo',
      confirmArchive: '¿Archivar esta programación?',
      confirmArchiveDescription:
        'Se cancelará el trabajo futuro. El historial y los registros de auditoría se conservarán.',
      confirmDelete: '¿Eliminar esta programación?',
      confirmDeleteDescription:
        'La programación se eliminará permanentemente. Las conversaciones anteriores y el trabajo generado permanecerán en el espacio de trabajo.',
      confirmUnshareDescription:
        'Los miembros del espacio de trabajo perderán de inmediato el acceso al prompt, la transcripción, la actividad de herramientas y el resultado.',
      confirmCancel: '¿Cancelar la ejecución activa?',
      confirmCancelDescription:
        'La ejecución se detendrá en el próximo punto seguro de cancelación.',
      actionCompleted: 'Programación actualizada',
      permissionDenied:
        'Puedes ver esta programación, pero solo su propietario o un administrador de programaciones puede cambiarla.',
      needsAttention:
        'Esta ejecución debe revisarse antes de continuar el trabajo sin supervisión.',
      runDetails: 'Detalles de la ejecución',
    },
    notifications: {
      completed: 'Ejecución programada completada',
      failed: 'La ejecución programada ha fallado',
      attention: 'La ejecución programada requiere atención',
      missed: 'Se perdió la ejecución única programada',
      viewRun: 'Ver ejecución',
      empty: 'Aún no hay notificaciones de ejecuciones.',
      unread: 'Sin leer',
      read: 'Leído',
      caughtUp: 'Has visto todas las notificaciones.',
    },
    errors: {
      generic: 'Algo salió mal. Inténtalo de nuevo.',
      permission: 'No tienes permiso para realizar esta acción.',
      disabled:
        'Los agentes programados aún no están habilitados en este espacio de trabajo.',
      credits: 'El espacio de trabajo no tiene créditos suficientes.',
      limit:
        'Este espacio de trabajo alcanzó su límite de programaciones activas.',
      runPending:
        'Ya hay una ejecución pendiente o en curso para esta programación.',
      invalidTime: 'Elige una hora futura válida.',
      notFound: 'Esta programación o ejecución ya no está disponible.',
    },
  },
} satisfies Record<'de' | 'es', ScheduledDetailedCopy>;
